import { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';

// ─── Session ID ───────────────────────────────────────────────────────────────
function getSessionId(): string {
  const key = 'shp_session_id';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(key, id);
  }
  return id;
}

function getDevice(): string {
  const ua = navigator.userAgent;
  if (/Mobi|Android/i.test(ua)) return 'mobile';
  if (/Tablet|iPad/i.test(ua)) return 'tablet';
  return 'desktop';
}

// ─── Track function ───────────────────────────────────────────────────────────
export async function trackEvent(
  eventType: string,
  eventLabel?: string,
  section?: string,
  extra?: Record<string, unknown>
) {
  try {
    await supabase.from('page_events').insert({
      session_id: getSessionId(),
      event_type: eventType,
      event_label: eventLabel ?? null,
      section: section ?? null,
      extra: extra ?? null,
      device: getDevice(),
      referrer: document.referrer || null,
    });
  } catch {
    // silent fail — tracking should never break the app
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────
interface TrackingContextValue {
  sessionId: string;
  track: typeof trackEvent;
}

const TrackingContext = createContext<TrackingContextValue>({
  sessionId: '',
  track: trackEvent,
});

export function useTracking() {
  return useContext(TrackingContext);
}

// ─── Provider ─────────────────────────────────────────────────────────────────
interface TrackingProviderProps {
  children: ReactNode;
}

export function TrackingProvider({ children }: TrackingProviderProps) {
  const sessionId = getSessionId();
  const entryTime = useRef(Date.now());
  const scrollDepthReported = useRef<Set<number>>(new Set());

  // 1. Page enter
  useEffect(() => {
    trackEvent('page_enter', 'Ana Sayfa', undefined, {
      referrer: document.referrer || 'direct',
      device: getDevice(),
      screen: `${window.innerWidth}x${window.innerHeight}`,
    });

    // 2. Page leave — time on site
    const handleLeave = () => {
      const seconds = Math.round((Date.now() - entryTime.current) / 1000);
      trackEvent('page_leave', 'Ana Sayfa', undefined, { seconds_on_page: seconds });
    };
    window.addEventListener('beforeunload', handleLeave);
    return () => window.removeEventListener('beforeunload', handleLeave);
  }, []);

  // 3. Scroll depth tracking
  useEffect(() => {
    const THRESHOLDS = [25, 50, 75, 100];

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = Math.round((scrollTop / docHeight) * 100);

      THRESHOLDS.forEach((threshold) => {
        if (pct >= threshold && !scrollDepthReported.current.has(threshold)) {
          scrollDepthReported.current.add(threshold);
          trackEvent('scroll_depth', `%${threshold}`, undefined, { depth_percent: threshold });
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 4. Section visibility tracking (IntersectionObserver)
  useEffect(() => {
    const SECTION_MAP: Record<string, string> = {
      'section-hero': 'Hero',
      'section-problem': 'Problem',
      'section-solution': 'Çözüm',
      'section-how': 'Nasıl Çalışır',
      'section-features': 'Özellikler',
      'section-packages': 'Paketler',
      'section-testimonials': 'Yorumlar',
      'section-faq': 'SSS',
      'section-cta': 'CTA',
    };

    const reported = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = (entry.target as HTMLElement).id;
          if (entry.isIntersecting && id && SECTION_MAP[id] && !reported.has(id)) {
            reported.add(id);
            trackEvent('section_view', SECTION_MAP[id], SECTION_MAP[id]);
          }
        });
      },
      { threshold: 0.3 }
    );

    Object.keys(SECTION_MAP).forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <TrackingContext.Provider value={{ sessionId, track: trackEvent }}>
      {children}
    </TrackingContext.Provider>
  );
}
